import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { PaisViewModel } from "src/app/_core/models/centros/pais.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-pais-autocomplete",
    templateUrl: "./pais-autocomplete.component.html",
    styleUrls: ["./pais-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PaisAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class PaisAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "País";
	@Input() placeholder = "Buscar país por código ISO o nombre";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código ISO2 o nombre del país";

	// Outputs
	@Output() paisSelected = new EventEmitter<PaisViewModel>();

	// State
	paises: PaisViewModel[] = [];
	paisId: number | null = null;
	selectedPais: PaisViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadPaises();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.paisId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de países
	 */
	private loadPaises(): void {
		this.masterService
			.getPaises()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (paises) => {
					this.paises = paises;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los países",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedPais =
			this.paisId != null
				? (this.paises.find((p) => p.id === this.paisId) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un país
	 */
	obtenerTextoPais = (pais: PaisViewModel | string): string => {
		if (typeof pais === "string") {
			return pais;
		}
		return pais && pais.codigoIso2 && pais.descripcion
			? `${pais.codigoIso2} - ${pais.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(pais: PaisViewModel | null): void {
		this.paisId = pais ? pais.id : null;
		this.selectedPais = pais;
		this.onChange(this.paisId);
		this.onTouched();
		if (pais) {
			this.paisSelected.emit(pais);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
